import { FC, useState } from 'react'
import { Box, styled } from '@mui/material'
import { getDecisionCommentValidationMessage, mobileScreenWidth } from 'utils'
import { isNilOrEmpty } from 'packages/core'
import MitigationControlList from './MitigationControlList'
import Button from 'components/Button'
import { Icon } from 'packages/eid-icons'
import { Spinner, ValidationMessage, ValidationMessages } from 'packages/eid-ui'
import DecisionComment from 'components/DecisionComment'
import { MUIDatePicker } from 'packages/eid-ui/DateTimePickers'
import { useTranslation } from 'react-i18next'
import { IMitigatingControl } from 'types'
import useSubcomponents from 'useSubcomponents'
import { Moment } from 'moment'
import moment from 'moment'

const StyledTitle = styled(Box)({
    paddingTop: '1.2rem',
    paddingBottom: '1.2rem',
    fontSize: '1.4rem',
    fontWeight: 'bold',
    color: '#000000',
})

const StyledSubmitButton = styled(Box)({
    '& >button': {
        backgroundColor: '#307fc1',
        '&:hover': {
            backgroundColor: '#307fc1',
            opacity: 0.95,
        },
        '& svg': {
            color: '#ffffff',
        },
    },
})

const StyledCancelButton = styled(Box)({
    '& >button': {
        backgroundColor: '#d9d9d9',
        '&:hover': {
            backgroundColor: '#d9d9d9',
            opacity: 0.95,
        },

        '& svg': {
            color: '#ffffff',
            width: '1.4rem',
            height: '1.4rem',
        },
    },
})

const EndDateContainer = styled(Box)({
    display: 'flex',
    width: '100%',
    alignItems: 'flex-end',
    '& svg': {
        color: '#307fc1',
        margin: '0.4rem 1.2rem 0.4rem 0rem',
    },
})

const DatePickerContainer = styled(Box)({
    width: '100%',
    borderBottom: '0.1rem solid transparent',
    '&:hover': {
        borderBottom: '0.1rem solid black',
    },
})

const PopoverContentContainer = styled(Box)({
    borderRadius: '0.5rem',
    padding: '1.2rem 2.4rem',
    boxShadow: '0 0.2rem 1.6rem 0 rgba(0, 0, 0, 0.15)',
    [`@media (min-width:${mobileScreenWidth})`]: {
        minWidth: '42rem',
    },
    [`@media (max-width:${mobileScreenWidth})`]: {
        width: '100%',
    },
})

export interface MitigationPopoverProps {
    currentApprovalStepId: string
    decisionFriendlyName: string
    onSubmit: (value: any) => Promise<any>
    isLoading?: boolean
    handleClose?: () => void
}

export const MitigateRisk: FC<MitigationPopoverProps> = (props) => {
    const { t } = useTranslation()

    const [decisionComment, setDecisionComment] = useState('')

    const [showErrors, setShowErrors] = useState(false)

    const [
        selectedMitigatingControls,
        setSelectedMitigatingControls,
    ] = useState<IMitigatingControl[]>([])
    const [selectedEndDate, setSelectedEndDate] = useState<Moment | null>(null)

    const { hasAccessToRiskMitigationEndDateControl } = useSubcomponents()

    const noMitigationControlSelected = selectedMitigatingControls.length === 0

    const validationMessage = getDecisionCommentValidationMessage(
        decisionComment,
    )
    const invalidComment = !isNilOrEmpty(validationMessage)

    const notSubmittable = noMitigationControlSelected || invalidComment

    return (
        <PopoverContentContainer>
            <StyledTitle>{props.decisionFriendlyName}</StyledTitle>

            <MitigationControlList
                onChange={setSelectedMitigatingControls}
                currentApprovalStepId={props.currentApprovalStepId}
            />

            {hasAccessToRiskMitigationEndDateControl && (
                <EndDateContainer
                    data-protectedsubcomponent={
                        hasAccessToRiskMitigationEndDateControl
                    }
                >
                    <Icon name="EndTime" />
                    <DatePickerContainer>
                        <MUIDatePicker
                            dateFormat="D MMMM YYYY"
                            minDate={moment(new Date()).format()}
                            value={selectedEndDate ? selectedEndDate : null}
                            handleChange={(e: any) => {
                                if (isNilOrEmpty(e)) {
                                    setSelectedEndDate(null)
                                    return
                                }
                                const date = moment(e)
                                const dateToSet = date
                                    .clone()
                                    .endOf('day')
                                    .subtract(1, 'minutes')
                                    .set('milliseconds', 0)
                                setSelectedEndDate(dateToSet)
                            }}
                            label={t('EndDate')}
                        />
                    </DatePickerContainer>
                </EndDateContainer>
            )}

            <Box my={3}>
                <DecisionComment
                    initialValue={decisionComment}
                    onChange={(comment: any) => setDecisionComment(comment)}
                />
            </Box>

            {showErrors && notSubmittable && (
                <ValidationMessages>
                    {selectedMitigatingControls.length === 0 && (
                        <ValidationMessage
                            message={t('MitigationControlRequired')}
                        />
                    )}
                    {invalidComment && (
                        <ValidationMessage message={validationMessage} />
                    )}
                </ValidationMessages>
            )}

            <Box my={2} display="flex">
                <StyledCancelButton>
                    <Button size="medium" onClick={props.handleClose}>
                        <Icon name="Close" />
                    </Button>
                    </StyledCancelButton>
                </Box>
                <StyledSubmitButton>
                    <Button
                        size="medium"
                        onClick={() => {
                            if (notSubmittable) {
                                setShowErrors(true)
                                return
                            }

                            let itemToSubmit: any = {
                                mitigatingControlIds: selectedMitigatingControls.map(
                                    (m) => m.id,
                                ),
                                comment: decisionComment,
                            }
                            if (hasAccessToRiskMitigationEndDateControl) {
                                itemToSubmit.endDateUtc = selectedEndDate
                            }

                            props.onSubmit(itemToSubmit)
                        }}
                    >
                        {props.isLoading ? (
                            <Spinner size={0.6} color="#ffffff" />
                        ) : (
                            <Icon name="Approve" />
                        )}
                    </Button>
                </StyledSubmitButton>
        </PopoverContentContainer>
    )
}
